import React from 'react'
import { ChevronRight, ChevronDown, Trash2 } from 'lucide-react'
import { format } from 'date-fns'
import { getFileIcon } from '../../utils/fileIcons'
import { formatNumber } from '../../utils/formatters'
import { getStatusConfig } from '../../utils/statusHelpers'

import type { RAGDocument } from '@/types/rag'

interface DocumentCardProps {
    doc: RAGDocument
    isSelected: boolean
    isExpanded: boolean
    onToggleSelect: () => void
    onExpand: () => void
    onDelete: (id: string, filename: string) => void
}

export const DocumentCard: React.FC<DocumentCardProps> = ({
    doc,
    isSelected,
    isExpanded,
    onToggleSelect,
    onExpand,
    onDelete
}) => {
    const statusConfig = getStatusConfig(doc.status)
    const StatusIcon = statusConfig.icon

    return (
        <div className={`bg-white/5 border border-white/10 rounded-lg p-4 transition-all ${isSelected ? 'border-indigo-500/50 bg-indigo-500/5' : ''}`}>
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                    <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={onToggleSelect}
                        className="rounded bg-white/10 border-white/20 text-indigo-500 focus:ring-indigo-500/50 focus:ring-offset-0"
                    />
                    <div className="flex items-center gap-2">
                        {getFileIcon(doc.filename, doc.mime_type)}
                        <span className="text-sm font-medium text-gray-200 truncate max-w-[150px]">
                            {doc.filename}
                        </span>
                    </div>
                </div>
                <button
                    onClick={onExpand}
                    className="p-1 text-gray-400 hover:text-white"
                >
                    {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                </button>
            </div>

            {/* Status & Metrics */}
            <div className="flex items-center justify-between mb-3">
                <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium border ${statusConfig.bg} ${statusConfig.color} ${statusConfig.border}`}>
                    <StatusIcon className={`w-3 h-3 ${statusConfig.animate ? 'animate-spin' : ''}`} />
                    {doc.status}
                </div>

                {doc.status === 'INDEXED' && (
                    <div className="flex gap-3 text-xs text-gray-500">
                        <span>{formatNumber(doc.chunks_count || 0)} chunks</span>
                        <span>{formatNumber(doc.total_tokens || 0)} tokens</span>
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between text-xs text-gray-500 border-t border-white/5 pt-3">
                <span className="font-mono">{format(new Date(doc.created_at), 'dd/MM/yyyy HH:mm')}</span>
                <button
                    onClick={(e) => {
                        e.stopPropagation()
                        onDelete(doc.id, doc.filename)
                    }}
                    className="flex items-center gap-1 text-red-400/80 hover:text-red-400 transition-colors"
                >
                    <Trash2 className="w-3.5 h-3.5" />
                    Delete
                </button>
            </div>

            {/* Error Message if Failed */}
            {doc.error_message && (
                <div className="mt-2 text-xs text-red-400 bg-red-500/5 p-2 rounded">
                    {doc.error_message}
                </div>
            )}
        </div>
    )
}
